import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Button,
  Search,
  EmptyState,
  CircularLoader,
  Banner,
  GroupIcon,
  PlusIcon,
  Pagination,
  useDocumentTitle,
} from '@forge/common';
import { usePartners } from '@/hooks/usePartners';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';
import CreatePartnerDialog from './components/CreatePartnerDialog';

const statusColor: Record<string, 'green' | 'blue' | 'orange' | 'gray'> = {
  active: 'green',
  onboarding: 'blue',
  paused: 'orange',
  retired: 'gray',
};

const PartnersPage = () => {
  useDocumentTitle('Partners — Partner Exchange — OneRing Portal');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [showCreate, setShowCreate] = useState(false);

  // Reset to page 1 whenever the search term changes so a filtered result
  // set is never rendered against a stale, out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const { partners, total, isLoading, error, isUsingMockData, reload } = usePartners({
    filters: { search: search.trim() || undefined, page, limit },
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">Partners</h2>
      <p className="text-sm text-color-tertiary mb-4">
        Utilities and organizations we exchange PGP-encrypted files with over SFTP.
      </p>

      {isUsingMockData && (
        <div className="mb-4">
          <MockDataBanner featureName="Partners" />
        </div>
      )}

      {error ? (
        <Banner
          variant="inline"
          type="error"
          title="Failed to load partners"
          description={error}
          canClose={false}
        />
      ) : (
        <Card>
          <CardHeader
            title="Partners"
            subtitle={`${total} partner${total !== 1 ? 's' : ''}`}
            slotRight={
              <div className="flex items-center gap-3">
                <Search
                  id="partner-search"
                  placeholder="Search partners..."
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button variant="primary" icon={<PlusIcon />} onClick={() => setShowCreate(true)}>
                  Add Partner
                </Button>
              </div>
            }
          />
          <CardContent>
            {isLoading ? (
              <div className="flex min-h-[240px] items-center justify-center">
                <CircularLoader size="large" />
              </div>
            ) : partners.length === 0 ? (
              <EmptyState icon={<GroupIcon />} description="No partners found" />
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Egress Note</TableCell>
                      <TableCell>Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {partners.map((partner) => (
                      <TableRow
                        key={partner.id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => navigate(`/partners/${partner.id}`)}
                      >
                        <TableCell>
                          <span className="font-mono text-sm">{partner.code}</span>
                        </TableCell>
                        <TableCell>{partner.displayName}</TableCell>
                        <TableCell>
                          <Badge color={statusColor[partner.status] ?? 'gray'}>
                            {partner.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-color-tertiary">
                            {partner.egressAllowlistNote ?? '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-color-secondary">
                            {new Date(partner.updatedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  currentPage={page}
                  totalItems={total}
                  totalPages={totalPages}
                  pageSize={limit}
                  pageSizeOptions={[25, 50, 100]}
                  showPageInfo
                  showPageSizeList
                  onPageChange={setPage}
                  onPageSizeChange={(newSize) => {
                    setPage(1);
                    setLimit(newSize);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {showCreate && (
        <CreatePartnerDialog
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            reload();
          }}
        />
      )}
    </div>
  );
};

export default PartnersPage;
